import { z } from 'zod';

/**
 * Identity Resolution DTOs
 *
 * Types and schemas for identity matching, record linking, and merging
 * Used by Service 32 (Entity Framework) for deduplication and identity resolution
 *
 * @see Issue #229 - Identity Resolution - Matching returning borrowers
 */

// ============================================================================
// ENUMS
// ============================================================================

/**
 * Match Criteria Types
 * Defines how a field should be compared during matching
 */
export const MatchCriteriaTypeSchema = z.enum([
  'EXACT', // Exact match (case-insensitive)
  'HASH', // SHA-256 hash comparison (for PII)
  'FUZZY_TEXT', // Levenshtein/Jaro-Winkler distance
  'PHONETIC', // Soundex/Metaphone matching
  'NORMALIZED', // Normalize before compare (trim, lowercase, remove special chars)
  'PARTIAL', // Contains/starts with matching
  'DATE_RANGE', // Date within range (e.g., +/- 1 day for DOB typos)
  'NUMERIC_RANGE', // Number within tolerance (e.g., +/- 5% for amounts)
]);
export type MatchCriteriaType = z.infer<typeof MatchCriteriaTypeSchema>;

/**
 * Match Actions
 * What action to take when a match is found
 */
export const MatchActionSchema = z.enum([
  'AUTO_LINK', // Automatically link records
  'FLAG_REVIEW', // Create review task for human
  'SUGGEST', // Show as suggestion in UI
  'BLOCK', // Block duplicate creation
  'IGNORE', // Do nothing, log only
]);
export type MatchAction = z.infer<typeof MatchActionSchema>;

/**
 * Match Levels
 * Confidence level of a match based on score thresholds
 */
export const IdentityMatchLevelSchema = z.enum([
  'EXACT', // Score >= 1.0
  'PROBABLE', // Score >= 0.85
  'POSSIBLE', // Score >= 0.70
  'NO_MATCH', // Score < 0.70
]);
export type IdentityMatchLevel = z.infer<typeof IdentityMatchLevelSchema>;

/**
 * Match Status
 * Workflow status of a match result
 */
export const MatchStatusSchema = z.enum([
  'PENDING', // Awaiting action
  'CONFIRMED', // Match confirmed, linked
  'REJECTED', // Match rejected, not same entity
  'AUTO_LINKED', // Automatically linked (high confidence)
  'IGNORED', // Dismissed without action
]);
export type MatchStatus = z.infer<typeof MatchStatusSchema>;

/**
 * Link Types
 * Type of relationship between linked records
 */
export const LinkTypeSchema = z.enum([
  'DUPLICATE', // Same entity, duplicate record
  'ALIAS', // Same entity, different representation
  'RELATED', // Related but distinct entities
  'HIERARCHY', // Parent-child relationship
]);
export type LinkType = z.infer<typeof LinkTypeSchema>;

/**
 * Merge Strategy Types
 * How to resolve field conflicts during merge
 */
export const MergeStrategyTypeSchema = z.enum([
  'KEEP_PRIMARY', // Keep value from primary record
  'KEEP_SECONDARY', // Keep value from secondary record
  'MERGE', // Combine values (for arrays, etc.)
  'CUSTOM', // Use custom value provided
]);
export type MergeStrategyType = z.infer<typeof MergeStrategyTypeSchema>;

/**
 * Fuzzy/Phonetic Algorithm Types
 */
export const MatchAlgorithmSchema = z.enum(['levenshtein', 'jaro-winkler', 'soundex', 'metaphone']);
export type MatchAlgorithm = z.infer<typeof MatchAlgorithmSchema>;

// ============================================================================
// MATCHING CRITERIA
// ============================================================================

/**
 * Single Matching Criterion
 * Defines how one field should be compared
 */
export const MatchingCriterionSchema = z.object({
  fieldName: z.string().min(1, 'Field name is required'),
  criteriaType: MatchCriteriaTypeSchema,
  weight: z.number().min(0).max(1),
  threshold: z.number().min(0).max(1).optional(),
  tolerance: z.number().optional(), // For range matching
  algorithm: MatchAlgorithmSchema.optional(),
});
export type MatchingCriterion = z.infer<typeof MatchingCriterionSchema>;

// ============================================================================
// MATCHING RULES
// ============================================================================

/**
 * Create Matching Rule Request
 */
export const CreateMatchingRuleSchema = z.object({
  name: z.string().min(1).max(100),
  description: z.string().max(500).optional(),
  priority: z.number().int().min(0).default(0),
  criteria: z.array(MatchingCriterionSchema).min(1, 'At least one criterion is required'),
  exactThreshold: z.number().min(0).max(1).default(1.0),
  probableThreshold: z.number().min(0).max(1).default(0.85),
  possibleThreshold: z.number().min(0).max(1).default(0.7),
  defaultAction: MatchActionSchema.default('FLAG_REVIEW'),
  isActive: z.boolean().default(true),
});
export type CreateMatchingRule = z.infer<typeof CreateMatchingRuleSchema>;

/**
 * Update Matching Rule Request
 */
export const UpdateMatchingRuleSchema = CreateMatchingRuleSchema.partial();
export type UpdateMatchingRule = z.infer<typeof UpdateMatchingRuleSchema>;

/**
 * Matching Rule Response
 */
export const MatchingRuleSchema = z.object({
  id: z.string(),
  businessId: z.string(),
  entityId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  priority: z.number().int(),
  criteria: z.array(MatchingCriterionSchema),
  exactThreshold: z.number(),
  probableThreshold: z.number(),
  possibleThreshold: z.number(),
  defaultAction: MatchActionSchema,
  isActive: z.boolean(),
  createdAt: z.union([z.string(), z.date()]),
  updatedAt: z.union([z.string(), z.date()]),
  createdBy: z.string(),
  updatedBy: z.string().nullable(),
});
export type MatchingRule = z.infer<typeof MatchingRuleSchema>;

/**
 * Test Matching Rule Request
 */
export const TestMatchingRuleRequestSchema = z.object({
  sourceData: z.record(z.string(), z.unknown()),
  targetData: z.record(z.string(), z.unknown()),
});
export type TestMatchingRuleRequest = z.infer<typeof TestMatchingRuleRequestSchema>;

/**
 * Test Matching Rule Response
 */
export const TestMatchingRuleResponseSchema = z.object({
  success: z.boolean(),
  overallScore: z.number(),
  matchLevel: IdentityMatchLevelSchema,
  suggestedAction: MatchActionSchema,
  fieldScores: z.record(
    z.string(),
    z.object({
      score: z.number(),
      criteriaType: z.string(),
      details: z.record(z.string(), z.unknown()),
    }),
  ),
});
export type TestMatchingRuleResponse = z.infer<typeof TestMatchingRuleResponseSchema>;

// ============================================================================
// IDENTITY RESOLUTION
// ============================================================================

/**
 * Resolve Identity Request
 */
export const ResolveIdentityRequestSchema = z.object({
  data: z.record(z.string(), z.unknown()).optional(),
  ruleIds: z.array(z.string()).optional(),
});
export type ResolveIdentityRequest = z.infer<typeof ResolveIdentityRequestSchema>;

/**
 * Find Matches Request
 */
export const FindMatchesRequestSchema = z.object({
  data: z.record(z.string(), z.unknown()),
  ruleIds: z.array(z.string()).optional(),
  limit: z.number().int().min(1).max(100).default(50),
  minScore: z.number().min(0).max(1).default(0.7),
});
export type FindMatchesRequest = z.infer<typeof FindMatchesRequestSchema>;

/**
 * Match Candidate
 * A potential matching record with scoring details
 */
export const MatchCandidateSchema = z.object({
  targetRecordId: z.string(),
  overallScore: z.number(),
  matchLevel: IdentityMatchLevelSchema,
  fieldScores: z.record(
    z.string(),
    z.object({
      score: z.number(),
      criteriaType: z.string(),
      details: z.record(z.string(), z.unknown()),
    }),
  ),
  suggestedAction: MatchActionSchema,
  targetRecord: z.record(z.string(), z.unknown()).optional(),
});
export type MatchCandidate = z.infer<typeof MatchCandidateSchema>;

/**
 * Resolve Identity Response
 */
export const ResolveIdentityResponseSchema = z.object({
  candidates: z.array(MatchCandidateSchema),
  totalMatches: z.number().int(),
});
export type ResolveIdentityResponse = z.infer<typeof ResolveIdentityResponseSchema>;

/**
 * Confirm Match Request
 */
export const ConfirmMatchRequestSchema = z.object({
  action: z.enum(['CONFIRM', 'AUTO_LINK']),
  reviewNotes: z.string().max(1000).optional(),
});
export type ConfirmMatchRequest = z.infer<typeof ConfirmMatchRequestSchema>;

/**
 * Reject Match Request
 */
export const RejectMatchRequestSchema = z.object({
  reviewNotes: z.string().max(1000).optional(),
});
export type RejectMatchRequest = z.infer<typeof RejectMatchRequestSchema>;

/**
 * Match Result Response
 */
export const MatchResultSchema = z.object({
  id: z.string(),
  businessId: z.string(),
  ruleId: z.string(),
  sourceRecordId: z.string(),
  targetRecordId: z.string(),
  overallScore: z.number(),
  matchLevel: IdentityMatchLevelSchema,
  fieldScores: z.record(z.string(), z.unknown()),
  status: MatchStatusSchema,
  suggestedAction: MatchActionSchema,
  resolvedAction: MatchActionSchema.nullable(),
  reviewedBy: z.string().nullable(),
  reviewedAt: z.union([z.string(), z.date()]).nullable(),
  reviewNotes: z.string().nullable(),
  entityLinkId: z.string().nullable(),
  createdAt: z.union([z.string(), z.date()]),
  updatedAt: z.union([z.string(), z.date()]),
});
export type MatchResult = z.infer<typeof MatchResultSchema>;

// ============================================================================
// ENTITY LINKS
// ============================================================================

/**
 * Link Records Request
 */
export const LinkRecordsRequestSchema = z.object({
  primaryRecordId: z.string(),
  secondaryRecordId: z.string(),
  linkType: LinkTypeSchema.default('DUPLICATE'),
  reason: z.string().max(500).optional(),
  confidence: z.number().min(0).max(1).default(1.0),
});
export type LinkRecordsRequest = z.infer<typeof LinkRecordsRequestSchema>;

/**
 * Entity Link Response
 */
export const EntityLinkSchema = z.object({
  id: z.string(),
  businessId: z.string(),
  entityId: z.string(),
  linkType: LinkTypeSchema,
  primaryRecordId: z.string(),
  secondaryRecordId: z.string(),
  isMerged: z.boolean(),
  mergedAt: z.union([z.string(), z.date()]).nullable(),
  mergedBy: z.string().nullable(),
  mergeStrategy: z.record(z.string(), z.unknown()).nullable(),
  confidence: z.number(),
  reason: z.string().nullable(),
  matchResultId: z.string().nullable(),
  createdAt: z.union([z.string(), z.date()]),
  updatedAt: z.union([z.string(), z.date()]),
  createdBy: z.string(),
  updatedBy: z.string().nullable(),
});
export type EntityLink = z.infer<typeof EntityLinkSchema>;

/**
 * List Entity Links Query
 */
export const ListEntityLinksQuerySchema = z.object({
  recordId: z.string().optional(),
  includeMerged: z.boolean().default(true),
  linkType: LinkTypeSchema.optional(),
  limit: z.number().int().min(1).max(100).default(50),
  offset: z.number().int().min(0).default(0),
});
export type ListEntityLinksQuery = z.infer<typeof ListEntityLinksQuerySchema>;

// ============================================================================
// MERGING
// ============================================================================

/**
 * Merge Field Strategy
 * How to resolve a specific field during merge
 */
export const MergeFieldStrategySchema = z.object({
  fieldName: z.string(),
  strategy: MergeStrategyTypeSchema,
  customValue: z.unknown().optional(),
});
export type MergeFieldStrategy = z.infer<typeof MergeFieldStrategySchema>;

/**
 * Preview Merge Request
 */
export const PreviewMergeRequestSchema = z.object({
  strategies: z.array(MergeFieldStrategySchema).optional(),
});
export type PreviewMergeRequest = z.infer<typeof PreviewMergeRequestSchema>;

/**
 * Preview Merge Response
 */
export const PreviewMergeResponseSchema = z.object({
  linkId: z.string(),
  primaryRecord: z.record(z.string(), z.unknown()),
  secondaryRecord: z.record(z.string(), z.unknown()),
  mergedData: z.record(z.string(), z.unknown()),
  conflicts: z.array(z.string()),
  suggestedStrategies: z.array(MergeFieldStrategySchema),
});
export type PreviewMergeResponse = z.infer<typeof PreviewMergeResponseSchema>;

/**
 * Execute Merge Request
 */
export const ExecuteMergeRequestSchema = z.object({
  strategies: z.array(MergeFieldStrategySchema),
});
export type ExecuteMergeRequest = z.infer<typeof ExecuteMergeRequestSchema>;

/**
 * Execute Merge Response
 */
export const ExecuteMergeResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  mergedRecordId: z.string(),
  mergedData: z.record(z.string(), z.unknown()),
  mergeSnapshot: z.object({
    primaryRecord: z.record(z.string(), z.unknown()),
    secondaryRecord: z.record(z.string(), z.unknown()),
  }),
});
export type ExecuteMergeResponse = z.infer<typeof ExecuteMergeResponseSchema>;

// ============================================================================
// IDENTITY RECORDS
// ============================================================================

/**
 * Create Identity Record Request
 */
export const CreateIdentityRecordSchema = z.object({
  identifierType: z.string().min(1),
  identifierValue: z.string().min(1), // Plain value, will be hashed server-side
  source: z.string().default('user_input'),
  sourceId: z.string().optional(),
  confidence: z.number().min(0).max(1).default(1.0),
});
export type CreateIdentityRecord = z.infer<typeof CreateIdentityRecordSchema>;

/**
 * Identity Record Response
 */
export const IdentityRecordSchema = z.object({
  id: z.string(),
  businessId: z.string(),
  entityId: z.string(),
  recordId: z.string(),
  identifierType: z.string(),
  identifierHash: z.string(), // Only hash is returned, never plain value
  normalizedPrefix: z.string().nullable(),
  isVerified: z.boolean(),
  verifiedAt: z.union([z.string(), z.date()]).nullable(),
  verifiedBy: z.string().nullable(),
  confidence: z.number(),
  source: z.string(),
  sourceId: z.string().nullable(),
  createdAt: z.union([z.string(), z.date()]),
  updatedAt: z.union([z.string(), z.date()]),
});
export type IdentityRecord = z.infer<typeof IdentityRecordSchema>;
